/**
 * Vessel Type Presets and Operations Mapping
 * Extracted from vesselAutoConfig.js to reduce file size
 */

// Comprehensive vessel type presets with ALL required fields for proper maintenance
export const vesselTypePresets = {
  tug: {
    towing_capable: true,
    carriesPassengers: false,
    specialEquipment: ['towing_winch', 'winch_lifting_gear', 'towing_bridle'],
    mandatoryEquipment: ['enhanced_navigation_lights', 'towing_lights', 'heavy_duty_fenders'],
    operations: ['towing_operations'],
    lifejacketType: 'foam', // Working deck preference
    stretcherFitted: false,
    resuscitatorFitted: false
  },
  pax: {
    towing_capable: false,
    carriesPassengers: true,
    specialEquipment: ['passenger_seating', 'passenger_safety_equipment'],
    mandatoryEquipment: ['pa_system', 'general_alarm', 'additional_liferafts'],
    operations: ['passenger_operations'],
    lifejacketType: 'foam',
    stretcherFitted: true,
    resuscitatorFitted: true
  },
  ctv: {
    towing_capable: false,
    carriesPassengers: true, // Technicians count as passengers
    specialEquipment: ['personnel_transfer_equipment', 'motion_compensated_gangway'],
    mandatoryEquipment: ['pa_system', 'high_speed_craft_equipment'],
    operations: ['personnel_transfer'],
    lifejacketType: 'inflatable', // Comfort for long transfers
    stretcherFitted: true,
    resuscitatorFitted: false
  },
  pilot: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: ['pilot_transfer_equipment', 'searchlight', 'pneumatic_fenders'],
    mandatoryEquipment: ['searchlight', 'immersion_suits', 'line_throwing_apparatus'],
    operations: ['pilot_transfer'],
    lifejacketType: 'inflatable',
    stretcherFitted: true, // Compact stretcher
    resuscitatorFitted: false
  },
  police: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: ['law_enforcement_equipment', 'blue_lights'],
    mandatoryEquipment: ['immersion_suits', 'enhanced_communications'],
    operations: ['patrol_operations'],
    policeBoat: true,
    enhancedLSARequired: true,
    lifejacketType: 'foam',
    stretcherFitted: true,
    resuscitatorFitted: true
  },
  rib: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: [],
    mandatoryEquipment: [],
    operations: [],
    lifejacketType: 'foam',
    stretcherFitted: false,
    resuscitatorFitted: false
  },
  survey: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: ['survey_equipment', 'winch_lifting_gear'],
    mandatoryEquipment: [],
    operations: ['survey_operations'],
    lifejacketType: 'foam',
    stretcherFitted: false,
    resuscitatorFitted: false
  },
  support: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: [],
    mandatoryEquipment: [],
    operations: [],
    lifejacketType: 'foam',
    stretcherFitted: false,
    resuscitatorFitted: false
  },
  patrol: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: [],
    mandatoryEquipment: [],
    operations: ['patrol_operations'],
    lifejacketType: 'foam',
    stretcherFitted: false,
    resuscitatorFitted: false
  },
  cargo: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: [],
    mandatoryEquipment: [],
    operations: ['cargo_operations'],
    lifejacketType: 'foam',
    stretcherFitted: false,
    resuscitatorFitted: false
  },
  utility: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: [],
    mandatoryEquipment: [],
    operations: [],
    lifejacketType: 'foam',
    stretcherFitted: false,
    resuscitatorFitted: false
  },
  multipurpose: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: [],
    mandatoryEquipment: [],
    operations: [], // User selects manually
    lifejacketType: 'foam',
    stretcherFitted: false,
    resuscitatorFitted: false
  },
  fishing_support: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: ['fishing_support_equipment'],
    mandatoryEquipment: [],
    operations: ['fishing_support'],
    lifejacketType: 'foam',
    stretcherFitted: false,
    resuscitatorFitted: false
  },
  hydrographic: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: ['survey_equipment', 'hydrographic_equipment'],
    mandatoryEquipment: [],
    operations: ['survey_operations'],
    lifejacketType: 'foam',
    stretcherFitted: false,
    resuscitatorFitted: false
  },
  dredger: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: ['dredging_equipment', 'heavy_machinery'],
    mandatoryEquipment: ['stability_monitoring'],
    operations: ['dredging_operations'],
    lifejacketType: 'foam',
    stretcherFitted: false,
    resuscitatorFitted: false
  },
  safety_standby: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: ['rescue_equipment', 'medical_equipment'],
    mandatoryEquipment: ['enhanced_first_aid', 'rescue_boat', 'searchlight'],
    operations: ['rescue_operations'],
    lifejacketType: 'foam',
    stretcherFitted: true,
    resuscitatorFitted: true
  },
  dive_support: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: ['dive_support_equipment', 'decompression_chamber'],
    mandatoryEquipment: ['dive_platform', 'emergency_dive_equipment'],
    operations: ['dive_support'],
    lifejacketType: 'foam',
    stretcherFitted: true,
    resuscitatorFitted: true
  },
  
  // Certificate-based vessel types (same operational requirements as legacy types)
  workboat: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: [],
    mandatoryEquipment: [],
    operations: [],
    lifejacketType: 'foam',
    stretcherFitted: false,
    resuscitatorFitted: false
  },
  workboat_towing_endorsed: {
    towing_capable: true,
    carriesPassengers: false,
    specialEquipment: ['towing_winch', 'winch_lifting_gear', 'towing_bridle'],
    mandatoryEquipment: ['enhanced_navigation_lights', 'towing_lights', 'heavy_duty_fenders'],
    operations: ['towing_operations'], // CRITICAL: Auto-select towing operations
    lifejacketType: 'foam', // Working deck preference
    stretcherFitted: false,
    resuscitatorFitted: false
  },
  light_duty_workboat: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: [],
    mandatoryEquipment: [],
    operations: [], // Light duty certificates cannot have towing/lifting/dangerous goods
    lifejacketType: 'foam',
    stretcherFitted: false,
    resuscitatorFitted: false
  },
  pilot_boat_dedicated: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: ['pilot_transfer_equipment', 'searchlight', 'pneumatic_fenders'],
    mandatoryEquipment: ['searchlight', 'immersion_suits', 'line_throwing_apparatus'],
    operations: ['pilot_transfer'], // CRITICAL: Auto-select pilot transfer operations
    lifejacketType: 'inflatable',
    stretcherFitted: true, // Compact stretcher
    resuscitatorFitted: false
  },
  workboat_pilot_endorsed: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: ['pilot_transfer_equipment', 'searchlight', 'pneumatic_fenders'],
    mandatoryEquipment: ['searchlight', 'immersion_suits', 'line_throwing_apparatus'],
    operations: ['pilot_transfer'], // CRITICAL: Auto-select pilot transfer operations
    lifejacketType: 'inflatable',
    stretcherFitted: true, // Compact stretcher
    resuscitatorFitted: false
  },
  police_boat: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: ['law_enforcement_equipment', 'blue_lights'],
    mandatoryEquipment: ['immersion_suits', 'enhanced_communications'],
    operations: ['patrol_operations'],
    policeBoat: true,
    enhancedLSARequired: true,
    lifejacketType: 'foam',
    stretcherFitted: true,
    resuscitatorFitted: true
  },
  
  // NEW: Additional certificate types
  non_self_propelled: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: ['emergency_towline', 'emergency_anchor', 'platform_marking'],
    mandatoryEquipment: ['emergency_towline', 'emergency_anchor_cable', 'emergency_lifebuoys', 'forward_marking', 'load_line_marking'],
    operations: [], // No commercial operations - platform only
    lifejacketType: 'foam',
    stretcherFitted: false,
    resuscitatorFitted: false,
    selfPropelled: false,
    isPlatform: true,
    requiresTowing: true,
    hasCrewRequirements: false,
    hasUniversalRequirements: false,
    requirementsEngine: 'SELECTIVE_WBC3_ASSESSMENT'
  },
  workboat_mgo_transfer_endorsed: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: ['mgo_transfer_equipment'],
    mandatoryEquipment: ['mgo_pump', 'mgo_hoses', 'spill_cleanup'],
    operations: ['mgo_transfer_ops'], // CRITICAL: Auto-select MGO transfer operations
    lifejacketType: 'foam',
    stretcherFitted: false,
    resuscitatorFitted: false
  },
  workboat_high_speed_endorsed: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: ['high_speed_equipment', 'individual_inboard_seating', 'vibration_control_equipment'],
    mandatoryEquipment: ['enhanced_stability_systems', 'shock_absorbent_seating', 'high_speed_headrests', 'high_speed_footrests'],
    operations: ['high_speed_ops'], // CRITICAL: Auto-select high-speed operations
    lifejacketType: 'inflatable', // High-speed preference
    stretcherFitted: false,
    resuscitatorFitted: false,
    highSpeedCapable: true,
    maxSpeed: 20, // Define high-speed threshold
    requiresAdvancedPowerboat: true // Master qualification requirement
  },
  // NOTE: Tender certificates removed - tenders operate under parent vessel SMS
  workboat_towing_pilot: {
    towing_capable: true,
    carriesPassengers: false,
    specialEquipment: ['towing_winch', 'winch_lifting_gear', 'towing_bridle', 'pilot_transfer_equipment', 'searchlight', 'pneumatic_fenders'],
    mandatoryEquipment: ['enhanced_navigation_lights', 'towing_lights', 'heavy_duty_fenders', 'searchlight', 'immersion_suits', 'line_throwing_apparatus'],
    operations: ['towing_operations', 'pilot_transfer'], // CRITICAL: Auto-select both operations
    lifejacketType: 'inflatable',
    stretcherFitted: true,
    resuscitatorFitted: false
  },
  workboat_towing_mgo_transfer: {
    towing_capable: true,
    carriesPassengers: false,
    specialEquipment: ['towing_winch', 'winch_lifting_gear', 'towing_bridle', 'mgo_transfer_equipment'],
    mandatoryEquipment: ['enhanced_navigation_lights', 'towing_lights', 'heavy_duty_fenders', 'mgo_pump', 'mgo_hoses', 'spill_cleanup'],
    operations: ['towing_operations', 'mgo_transfer_ops'], // CRITICAL: Auto-select both operations
    lifejacketType: 'foam',
    stretcherFitted: false,
    resuscitatorFitted: false
  },
  workboat_pilot_high_speed: {
    towing_capable: false,
    carriesPassengers: false,
    specialEquipment: ['pilot_transfer_equipment', 'searchlight', 'pneumatic_fenders', 'high_speed_equipment', 'individual_inboard_seating', 'vibration_control_equipment'],
    mandatoryEquipment: ['searchlight', 'immersion_suits', 'line_throwing_apparatus', 'enhanced_stability_systems', 'shock_absorbent_seating', 'high_speed_headrests'],
    operations: ['pilot_transfer', 'high_speed_ops'], // CRITICAL: Auto-select both operations
    lifejacketType: 'inflatable',
    stretcherFitted: true,
    resuscitatorFitted: false,
    highSpeedCapable: true,
    maxSpeed: 20, // Define high-speed threshold
    requiresAdvancedPowerboat: true // Master qualification requirement
  },
  workboat_multiple_custom: {
    towing_capable: null, // User decides
    carriesPassengers: null, // User decides
    specialEquipment: [], // User selects manually
    mandatoryEquipment: [], // User selects manually
    operations: [], // User selects manually
    lifejacketType: 'foam', // Default - user can change
    stretcherFitted: false, // Default - user can change
    resuscitatorFitted: false, // Default - user can change
    requiresManualConfig: true // Flag to trigger manual configuration UI
  },
  
  // Other/Specialized vessel type - user configures everything manually
  other: {
    towing_capable: null,              // User decides
    carriesPassengers: null,           // User decides
    specialEquipment: [],              // User selects manually
    mandatoryEquipment: [],            // User selects manually
    operations: [],                    // User selects manually
    lifejacketType: 'foam',            // Default - user can change
    stretcherFitted: false,            // Default - user can change
    resuscitatorFitted: false,         // Default - user can change
    requiresManualConfig: true         // Flag to trigger manual configuration UI
  }
};

// Vessel type to operations mapping
export const vesselTypeOperations = {
  'rib': [],                        // General purpose
  'tug': ['towing_operations'],
  'pilot': ['pilot_transfer'],
  'police': [],                     // Police/law enforcement operations
  'pax': [],                        // Passenger operations implied
  'ctv': ['personnel_transfer'],
  'survey': ['survey_operations'],
  'support': [],                    // General support
  'patrol': ['patrol_operations'],
  'cargo': ['cargo_operations'],
  'utility': [],                    // General utility
  'multipurpose': [],               // User selects manually
  'fishing_support': [],            // Fishing support operations
  'hydrographic': ['survey_operations'], // Similar to survey
  'dredger': [],                    // Dredging operations
  'safety_standby': [],             // Safety/rescue operations
  'dive_support': ['dive_support'],  // Diving support operations
  
  // Certificate-based vessel types (same operational requirements as legacy types)
  'workboat': [],                   // General workboat
  'light_duty_workboat': [],        // Restricted operations
  'pilot_boat_dedicated': ['pilot_transfer'],  // CRITICAL: Auto-select pilot transfer
  'police_boat': ['patrol_operations'], // Police operations
  'non_self_propelled': [],         // Non-self-propelled operations
  
  // Single endorsements
  'workboat_towing_endorsed': ['towing_operations'], // CRITICAL: Auto-select towing operations
  'workboat_pilot_endorsed': ['pilot_transfer'], // CRITICAL: Auto-select pilot transfer  
  'workboat_mgo_transfer_endorsed': ['mgo_transfer_ops'], // CRITICAL: Auto-select MGO transfer operations
  'workboat_high_speed_endorsed': ['high_speed_ops'], // CRITICAL: Auto-select high-speed operations
  // NOTE: Tender operations removed - tenders operate under parent vessel SMS
  
  // Multiple endorsements  
  'workboat_towing_pilot': ['towing_operations', 'pilot_transfer'], // CRITICAL: Auto-select both
  'workboat_towing_mgo_transfer': ['towing_operations', 'mgo_transfer_ops'], // CRITICAL: Auto-select both
  'workboat_pilot_high_speed': ['pilot_transfer', 'high_speed_ops'], // CRITICAL: Auto-select both operations
  'workboat_multiple_custom': [], // Manual selection for custom endorsements
  'other': []                     // Manual selection - no auto-operations
};